import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import MailOutlined from '@mui/icons-material/MailOutlined';
import API_BASE from '../../config/api';

function getRoleColor(role) {
  switch (role) {
    case 'Owner':       return '#E5C04B';
    case 'Admin':       return '#39FF14';
    case 'Contributor': return '#2196F3';
    case 'Commentator': return '#888888';
    default:            return '#888888';
  }
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

export default function MembersSettings() {
  const { token, user } = useAuth();
  const { activeWorkspace, fetchWorkspaces } = useWorkspace();
  const members = activeWorkspace?.members || [];

  const myId = (user?._id || user?.id || '').toString();
  const myMember = members.find((m) => m.user_id?.toString() === myId);
  const isOwner = myMember?.role === 'Owner';

  const [showInvitePanel, setShowInvitePanel] = useState(false);
  const [memberProfiles, setMemberProfiles] = useState({});
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [transferTargetId, setTransferTargetId] = useState('');
  const [showTransferConfirm, setShowTransferConfirm] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [isWorking, setIsWorking] = useState(false);

  // Fetch personnel to build name lookup
  useEffect(() => {
    if (!token || !activeWorkspace?._id) return;
    axios
      .get(`${API_BASE}/api/personnel?workspace_id=${activeWorkspace._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const lookup = {};
        res.data.forEach((p) => {
          if (p.user_id) lookup[p.user_id.toString()] = p;
        });
        setMemberProfiles(lookup);
      })
      .catch(() => {});
  }, [token, activeWorkspace?._id]);

  function getMemberName(member) {
    const profile = memberProfiles[member.user_id?.toString()];
    if (profile?.first_name || profile?.last_name) {
      return `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
    }
    return null;
  }

  function getMemberInitial(member) {
    const name = getMemberName(member);
    if (name) return name[0].toUpperCase();
    return (member.user_id?.toString() || '?').slice(-1).toUpperCase();
  }

  function showSuccess(msg) {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  }

  async function handleRemoveMember(member) {
    setIsWorking(true);
    setActionError(null);
    try {
      const updatedMembers = activeWorkspace.members.filter(
        (m) => m.user_id?.toString() !== member.user_id?.toString()
      );
      await axios.put(
        `${API_BASE}/api/workspaces/${activeWorkspace._id}`,
        { members: updatedMembers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchWorkspaces();
      setConfirmRemoveId(null);
      showSuccess('Member removed');
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to remove member.');
    } finally {
      setIsWorking(false);
    }
  }

  async function handleTransferOwnership() {
    if (!transferTargetId) return;
    setIsWorking(true);
    setActionError(null);
    try {
      const updatedMembers = activeWorkspace.members.map((m) => {
        const uid = m.user_id?.toString();
        if (uid === transferTargetId) return { ...m, role: 'Owner' };
        if (uid === myId) return { ...m, role: 'Admin' };
        return m;
      });
      await axios.put(
        `${API_BASE}/api/workspaces/${activeWorkspace._id}`,
        { members: updatedMembers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchWorkspaces();
      setShowTransferConfirm(false);
      setTransferTargetId('');
      showSuccess('Ownership transferred successfully');
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to transfer ownership.');
      setShowTransferConfirm(false);
    } finally {
      setIsWorking(false);
    }
  }

  const transferCandidates = members.filter(
    (m) => m.user_id?.toString() !== myId && m.role !== 'Commentator'
  );

  const selectedTransferMember = members.find(
    (m) => m.user_id?.toString() === transferTargetId
  );
  const selectedTransferName = selectedTransferMember
    ? (getMemberName(selectedTransferMember) || `Member …${selectedTransferMember.user_id?.toString().slice(-4)}`)
    : '';

  return (
    <div style={{ maxWidth: '640px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 500,
          fontSize: '14px',
          color: '#888888',
        }}>
          {members.length} {members.length === 1 ? 'member' : 'members'}
        </span>
        <InviteButton onClick={() => setShowInvitePanel(true)} />
      </div>

      {/* Invite info panel */}
      {showInvitePanel && (
        <div style={{
          backgroundColor: 'rgba(229,192,75,0.06)',
          border: '1px solid rgba(229,192,75,0.15)',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '4px',
        }}>
          <MailOutlined style={{ fontSize: 24, color: '#E5C04B', display: 'block', marginBottom: '12px' }} />
          <div style={{
            fontFamily: "'Roboto', sans-serif",
            fontWeight: 700, fontSize: '16px', color: '#E5C04B',
            marginBottom: '8px',
          }}>
            Invite Members
          </div>
          <div style={{
            fontFamily: "'Roboto', sans-serif",
            fontSize: '14px', color: '#888888',
            marginBottom: '16px', lineHeight: '1.5',
          }}>
            Email invites are coming in a future update. To add members now:
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              'Share your workspace name with them',
              'Ask them to register at the app URL',
              'They will appear here once registered',
            ].map((text, i) => (
              <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <div style={{
                  width: '20px', height: '20px', borderRadius: '50%',
                  backgroundColor: '#39FF14',
                  color: '#1a1a1a',
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 700, fontSize: '11px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {i + 1}
                </div>
                <span style={{
                  fontFamily: "'Roboto', sans-serif",
                  fontSize: '13px', color: '#F5F5F5',
                  lineHeight: '20px',
                }}>
                  {text}
                </span>
              </div>
            ))}
          </div>
          <div style={{
            fontFamily: "'Roboto', sans-serif",
            fontSize: '12px', color: '#565656',
            fontStyle: 'italic', marginTop: '12px', lineHeight: '1.5',
          }}>
            Full invite system with pending member management coming in Phase 3.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
            <button
              onClick={() => setShowInvitePanel(false)}
              style={{
                background: 'transparent',
                border: '1px solid #565656',
                color: '#F5F5F5',
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 500, fontSize: '13px',
                padding: '7px 16px', borderRadius: '4px', cursor: 'pointer',
              }}
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Global success */}
      {successMsg && (
        <div style={{
          backgroundColor: 'rgba(57,255,20,0.08)',
          border: '1px solid rgba(57,255,20,0.3)',
          borderRadius: '6px',
          padding: '10px 14px',
          fontFamily: "'Roboto', sans-serif",
          fontSize: '13px',
          color: '#39FF14',
        }}>
          {successMsg}
        </div>
      )}

      {/* Global error */}
      {actionError && (
        <div style={{
          backgroundColor: 'rgba(191,38,52,0.08)',
          border: '1px solid rgba(191,38,52,0.3)',
          borderRadius: '6px',
          padding: '10px 14px',
          fontFamily: "'Roboto', sans-serif",
          fontSize: '13px',
          color: '#BF2634',
        }}>
          {actionError}
        </div>
      )}

      {/* Member list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {members.length === 0 && (
          <div style={{
            padding: '32px',
            textAlign: 'center',
            fontFamily: "'Roboto', sans-serif",
            fontSize: '14px',
            color: '#565656',
          }}>
            No members found
          </div>
        )}
        {members.map((m) => {
          const uid = m.user_id?.toString();
          const isMe = uid === myId;
          const isThisOwner = m.role === 'Owner';
          const showRemove = !isMe && !isThisOwner;
          const isConfirming = confirmRemoveId === uid;
          const name = getMemberName(m);
          const displayName = name || `Member …${uid?.slice(-4) || '??'}`;
          const initial = getMemberInitial(m);
          const roleColor = getRoleColor(m.role);

          return (
            <div key={uid || m._id}>
              {/* Member row */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                padding: '12px 16px',
                backgroundColor: isConfirming ? 'rgba(191,38,52,0.04)' : '#2a2a2a',
                borderRadius: isConfirming ? '6px 6px 0 0' : '6px',
                border: `1px solid ${isConfirming ? 'rgba(191,38,52,0.4)' : '#3a3a3a'}`,
                borderBottom: isConfirming ? 'none' : undefined,
                transition: 'all 0.15s ease',
              }}>
                {/* Avatar */}
                <div style={{
                  width: '36px', height: '36px',
                  borderRadius: '50%',
                  backgroundColor: '#333333',
                  border: `2px solid ${roleColor}33`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 700, fontSize: '14px', color: '#888888',
                  flexShrink: 0,
                }}>
                  {initial}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                    <span style={{
                      fontFamily: "'Roboto', sans-serif",
                      fontWeight: 500, fontSize: '14px', color: '#F5F5F5',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {displayName}
                    </span>
                    {isMe && (
                      <span style={{
                        fontFamily: "'Roboto', sans-serif",
                        fontSize: '11px', color: '#39FF14',
                        backgroundColor: 'rgba(57,255,20,0.1)',
                        padding: '1px 7px', borderRadius: '10px', flexShrink: 0,
                      }}>
                        you
                      </span>
                    )}
                  </div>
                  <div style={{
                    fontFamily: 'monospace', fontSize: '11px', color: '#444444',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {uid}
                  </div>
                  <div style={{
                    fontFamily: "'Roboto', sans-serif", fontSize: '12px', color: '#565656', marginTop: '1px',
                  }}>
                    Joined {formatDate(m.joined_at)}
                  </div>
                </div>

                {/* Role badge */}
                <div style={{
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 600, fontSize: '11px',
                  color: roleColor, backgroundColor: `${roleColor}18`,
                  padding: '3px 10px', borderRadius: '4px',
                  letterSpacing: '0.5px', flexShrink: 0,
                }}>
                  {m.role}
                </div>

                {/* Remove button */}
                {showRemove && (
                  <RemoveBtn
                    active={isConfirming}
                    onClick={() => setConfirmRemoveId(isConfirming ? null : uid)}
                  />
                )}
              </div>

              {/* Inline remove confirmation */}
              {isConfirming && (
                <div style={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid rgba(191,38,52,0.4)',
                  borderTop: 'none',
                  borderRadius: '0 0 6px 6px',
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                }}>
                  <span style={{
                    fontFamily: "'Roboto', sans-serif",
                    fontSize: '13px', color: '#888888',
                  }}>
                    Remove <strong style={{ color: '#F5F5F5' }}>{displayName}</strong> from workspace?
                  </span>
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    <button
                      onClick={() => setConfirmRemoveId(null)}
                      style={{
                        background: 'transparent',
                        border: '1px solid #3a3a3a',
                        color: '#888888',
                        fontFamily: "'Roboto', sans-serif",
                        fontWeight: 500, fontSize: '13px',
                        padding: '6px 14px', borderRadius: '4px', cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      disabled={isWorking}
                      onClick={() => handleRemoveMember(m)}
                      style={{
                        backgroundColor: '#BF2634',
                        border: 'none',
                        color: '#FFFFFF',
                        fontFamily: "'Roboto', sans-serif",
                        fontWeight: 500, fontSize: '13px',
                        padding: '6px 14px', borderRadius: '4px',
                        cursor: isWorking ? 'default' : 'pointer',
                        opacity: isWorking ? 0.7 : 1,
                      }}
                    >
                      {isWorking ? 'Removing…' : 'Remove'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pending invites */}
      <div style={{ borderTop: '1px solid #3a3a3a', paddingTop: '24px' }}>
        <div style={{
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 600, fontSize: '12px',
          color: '#888888', letterSpacing: '1.5px',
          textTransform: 'uppercase', marginBottom: '12px',
        }}>
          Pending Invites
        </div>
        <div style={{
          padding: '16px 20px',
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          border: '1px solid #3a3a3a',
          fontFamily: "'Roboto', sans-serif",
          fontSize: '14px', color: '#565656', fontStyle: 'italic',
        }}>
          Invite tracking coming soon
        </div>
      </div>

      {/* Transfer Ownership — Owner only */}
      {isOwner && (
        <div style={{ marginTop: '12px' }}>
          <div style={{
            fontFamily: "'Roboto', sans-serif",
            fontWeight: 500, fontSize: '11px',
            color: '#E5C04B', letterSpacing: '2px',
            textTransform: 'uppercase', marginBottom: '10px',
          }}>
            Transfer Ownership
          </div>
          <div style={{
            fontFamily: "'Roboto', sans-serif",
            fontSize: '13px', color: '#888888',
            marginBottom: '16px', lineHeight: '1.5',
          }}>
            Transfer workspace ownership to another member. You will become an Admin.
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <select
              value={transferTargetId}
              onChange={(e) => setTransferTargetId(e.target.value)}
              style={{
                flex: 1,
                backgroundColor: '#1a1a1a',
                border: '1px solid #3a3a3a',
                borderRadius: '4px',
                padding: '10px 14px',
                color: transferTargetId ? '#F5F5F5' : '#888888',
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 400, fontSize: '14px',
                outline: 'none',
                cursor: 'pointer',
                appearance: 'none',
                WebkitAppearance: 'none',
                backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23565656' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")",
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                paddingRight: '32px',
              }}
            >
              <option value="" style={{ backgroundColor: '#1a1a1a', color: '#888888' }}>
                Select new owner…
              </option>
              {transferCandidates.map((m) => {
                const uid = m.user_id?.toString();
                const name = getMemberName(m) || `Member …${uid?.slice(-4)}`;
                return (
                  <option key={uid} value={uid} style={{ backgroundColor: '#1a1a1a', color: '#F5F5F5' }}>
                    {name} ({m.role})
                  </option>
                );
              })}
            </select>

            <TransferButton
              disabled={!transferTargetId || isWorking}
              onClick={() => setShowTransferConfirm(true)}
            />
          </div>
        </div>
      )}

      {/* Transfer confirmation overlay */}
      {showTransferConfirm && (
        <div
          onClick={() => setShowTransferConfirm(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 4000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#2a2a2a',
              border: '1px solid #3a3a3a',
              borderRadius: '10px',
              padding: '32px',
              maxWidth: '440px',
              width: '90vw',
              boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
            }}
          >
            <div style={{
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 700, fontSize: '18px', color: '#FFFFFF',
              marginBottom: '16px',
            }}>
              Transfer Ownership
            </div>
            <div style={{
              fontFamily: "'Roboto', sans-serif",
              fontSize: '14px', color: '#888888',
              lineHeight: '1.6', marginBottom: '24px',
            }}>
              Are you sure you want to transfer ownership to{' '}
              <strong style={{ color: '#E5C04B' }}>{selectedTransferName}</strong>?
              {' '}You will become an Admin and lose Owner privileges.
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowTransferConfirm(false)}
                style={{
                  background: 'transparent',
                  border: '1px solid #565656',
                  color: '#F5F5F5',
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 500, fontSize: '14px',
                  padding: '10px 20px', borderRadius: '4px', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                disabled={isWorking}
                onClick={handleTransferOwnership}
                style={{
                  backgroundColor: isWorking ? '#a08a35' : '#E5C04B',
                  border: 'none',
                  color: '#1a1a1a',
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 700, fontSize: '14px',
                  padding: '10px 20px', borderRadius: '4px',
                  cursor: isWorking ? 'default' : 'pointer',
                  transition: 'background-color 0.15s ease',
                }}
              >
                {isWorking ? 'Transferring…' : 'Confirm Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InviteButton({ onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      style={{
        backgroundColor: hovered ? 'rgba(57,255,20,0.08)' : 'transparent',
        border: '1px solid #39FF14',
        color: '#39FF14',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500, fontSize: '13px',
        padding: '8px 16px', borderRadius: '4px',
        cursor: 'pointer', transition: 'all 0.15s ease',
      }}
    >
      + Invite Member
    </button>
  );
}

function RemoveBtn({ active, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: active
          ? 'rgba(191,38,52,0.12)'
          : hovered ? 'rgba(191,38,52,0.08)' : 'transparent',
        border: `1px solid ${active || hovered ? '#BF2634' : '#3a3a3a'}`,
        color: active || hovered ? '#BF2634' : '#565656',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500, fontSize: '12px',
        padding: '5px 12px', borderRadius: '4px',
        cursor: 'pointer', flexShrink: 0,
        transition: 'all 0.15s ease',
      }}
    >
      Remove
    </button>
  );
}

function TransferButton({ disabled, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: !disabled && hovered ? 'rgba(229,192,75,0.1)' : 'transparent',
        border: `1px solid ${disabled ? '#3a3a3a' : '#E5C04B'}`,
        color: disabled ? '#565656' : '#E5C04B',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500, fontSize: '14px',
        padding: '10px 20px', borderRadius: '4px',
        cursor: disabled ? 'default' : 'pointer',
        flexShrink: 0, transition: 'all 0.15s ease',
      }}
    >
      Transfer Ownership
    </button>
  );
}
